package com.trevorwiebe.timesheet.loads.presentation.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.gestures.scrollBy
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.layout.boundsInRoot
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.trevorwiebe.timesheet.core.presentation.common.rememberTextSharer
import com.trevorwiebe.timesheet.loads.domain.model.Load
import kotlinx.coroutines.delay
import kotlinx.datetime.LocalDate

private val GUTTER_WIDTH = 96.dp
// Matches the height of a single-line load row so empty and occupied sections line up.
private val EMPTY_SECTION_MIN_HEIGHT = 36.dp
// How close to the board's top/bottom the drag must get before the board auto-scrolls vertically.
private val VERTICAL_EDGE_ZONE = 56.dp

@Composable
fun DispatchBoard(
    state: LoadsState,
    date: LocalDate,
    dragState: LoadDragState,
    bottomPadding: Dp,
    contentPadding: PaddingValues = PaddingValues(0.dp),
    onEvent: (LoadsEvent) -> Unit,
    modifier: Modifier = Modifier,
) {
    val scrollState = rememberScrollState()
    var boardBounds by remember { mutableStateOf<Rect?>(null) }
    val edgePx = with(LocalDensity.current) { VERTICAL_EDGE_ZONE.toPx() }

    // Auto-scroll the board vertically while a drag hovers near its top/bottom edge, so drivers
    // below the fold are reachable without dropping.
    LaunchedEffect(dragState.isDragging) {
        while (dragState.isDragging) {
            val bounds = boardBounds
            val pointer = dragState.pointer
            if (bounds != null && pointer.x in bounds.left..bounds.right) {
                val step = when {
                    pointer.y < bounds.top + edgePx -> -12f
                    pointer.y > bounds.bottom - edgePx -> 12f
                    else -> 0f
                }
                if (step != 0f) scrollState.scrollBy(step)
            }
            delay(16)
        }
    }

    Column(
        modifier = modifier
            .fillMaxSize()
            .onGloballyPositioned { boardBounds = it.boundsInRoot() }
            .verticalScroll(scrollState)
            .padding(contentPadding),
    ) {
        DispatchZone(
            title = "Unassigned",
            zoneKey = "${date}_pool",
            date = date,
            employeeId = "",
            loads = state.dispatchPool(date),
            confirmable = false,
            state = state,
            dragState = dragState,
            onEvent = onEvent,
        )
        state.dispatchUsers.forEach { user ->
            DispatchZone(
                title = user.displayName,
                zoneKey = "${date}_${user.id}",
                date = date,
                employeeId = user.id,
                loads = state.dispatchLoadsFor(user.id, date),
                confirmable = true,
                state = state,
                dragState = dragState,
                onEvent = onEvent,
            )
        }
        if (state.dispatchCarriers.isNotEmpty()) {
            val shareText = rememberTextSharer()
            HorizontalDivider(thickness = 3.dp)
            state.dispatchCarriers.forEach { carrier ->
                DispatchZone(
                    title = carrier.companyName.ifEmpty { carrier.contactName },
                    zoneKey = "${date}_carrier_${carrier.id}",
                    date = date,
                    employeeId = "",
                    carrierId = carrier.id,
                    loads = state.carrierLoadsFor(carrier.id, date),
                    confirmable = false,
                    state = state,
                    dragState = dragState,
                    onEvent = onEvent,
                    onShare = { load -> shareText(state.carrierShareText(load)) },
                )
            }
        }
        Spacer(Modifier.height(bottomPadding + BOARD_BOTTOM_CLEARANCE))
    }
}

@Composable
private fun DispatchZone(
    title: String,
    zoneKey: String,
    date: LocalDate,
    employeeId: String,
    loads: List<Load>,
    confirmable: Boolean,
    state: LoadsState,
    dragState: LoadDragState,
    onEvent: (LoadsEvent) -> Unit,
    carrierId: String = "",
    onShare: ((Load) -> Unit)? = null,
) {
    val isOff = employeeId.isNotEmpty() && state.isUserOffOn(employeeId, date)
    val isHovered = dragState.hoveredZoneKey == zoneKey
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .then(
                // Off users never register as a drop zone, so loads can't be dropped on them.
                if (isOff) Modifier
                else Modifier.loadDropTarget(dragState, zoneKey, date, employeeId, carrierId)
            )
            .then(
                if (isHovered) {
                    Modifier.border(2.dp, MaterialTheme.colorScheme.tertiary, RoundedCornerShape(16.dp))
                } else Modifier
            ),
    ) {
        HorizontalDivider()
        Row(verticalAlignment = Alignment.CenterVertically) {
            SectionGutter(title)
            val first = loads.firstOrNull()
            if (first != null) {
                ZoneCard(first, confirmable, loads.size > 1, state, dragState, onEvent, Modifier.weight(1f), onShare)
            } else if (isOff) {
                // The "Off" tag sits where a dropped load would land.
                OffPill()
                Spacer(Modifier.weight(1f).height(EMPTY_SECTION_MIN_HEIGHT))
            } else {
                // Empty zone: keep a real, min-height drop landing area next to the name.
                Spacer(Modifier.weight(1f).height(EMPTY_SECTION_MIN_HEIGHT))
            }
        }
        loads.drop(1).forEachIndexed { index, load ->
            Row {
                Spacer(Modifier.width(GUTTER_WIDTH))
                ZoneCard(
                    load = load,
                    confirmable = confirmable,
                    showBottomDivider = index < loads.size - 2,
                    state = state,
                    dragState = dragState,
                    onEvent = onEvent,
                    modifier = Modifier.weight(1f),
                    onShare = onShare,
                )
            }
        }
    }
}

/** One draggable load row: dims in place while its ghost is in flight. */
@Composable
private fun ZoneCard(
    load: Load,
    confirmable: Boolean,
    showBottomDivider: Boolean,
    state: LoadsState,
    dragState: LoadDragState,
    onEvent: (LoadsEvent) -> Unit,
    modifier: Modifier = Modifier,
    onShare: ((Load) -> Unit)? = null,
) {
    val isLifted = dragState.draggedLoad?.id == load.id
    LoadCard(
        load = load,
        locations = state.locations,
        onClick = { onEvent(LoadsEvent.OnEditLoad(load)) },
        onToggleConfirm = if (confirmable) {
            { onEvent(LoadsEvent.OnToggleDispatchConfirmed(load)) }
        } else null,
        onShare = onShare?.let { share -> { share(load) } },
        showBottomDivider = showBottomDivider,
        startPadding = 0.dp,
        modifier = modifier
            .graphicsLayer { alpha = if (isLifted) 0.35f else 1f }
            .loadDragSource(dragState, load) { dropped, zone ->
                onEvent(LoadsEvent.OnMoveLoad(dropped, zone.date, zone.employeeId, zone.carrierId))
            },
    )
}

@Composable
private fun SectionGutter(title: String) {
    Text(
        text = title.substringBefore(' '),
        style = MaterialTheme.typography.titleSmall,
        fontWeight = FontWeight.Bold,
        color = MaterialTheme.colorScheme.tertiary,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis,
        modifier = Modifier.width(GUTTER_WIDTH).padding(start = 8.dp, end = 4.dp),
    )
}

@Composable
private fun OffPill() {
    Text(
        text = "Off",
        style = MaterialTheme.typography.labelSmall,
        fontWeight = FontWeight.Bold,
        color = MaterialTheme.colorScheme.onTertiary,
        modifier = Modifier
            // Matches the card's status-dot column so the pill lines up with pickup names.
            .padding(start = 10.dp)
            .clip(CircleShape)
            .background(MaterialTheme.colorScheme.tertiary)
            .padding(horizontal = 8.dp, vertical = 2.dp),
    )
}
